// Unpublished Work © 2025 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {
    AssistanceTooltip,
    Box,
    IconButton,
    Link,
    MultiSelect,
    Stack,
    Typography
} from '@deere/isg.component-library';
import {
    IconInfoAlert,
    IconRemove,
    IconDotFilled,
    IconNewWindow
} from '@deere/icons';
import {connect} from 'react-redux';
import {
    deleteSelectedOperators,
    selectOperator
} from 'Src/ui-core/store/actions/setup-workboard';
import {getOvertimeThresholds} from 'OnLabor/workboard/utils/overtime-status';
import {
    getGroupedFormattedOperators,
    getFormattedOperators
} from 'OnLabor/workboard/utils/operator-utils';
import {
    SelectAllConfirmation
} from '../../utils/setup-workboard';
import {replaceTranslationNames} from 'Utils/translation-utils';

const REMOVE_ICON_STYLE = {
    style: {
        height: '20px',
        width: '20px'
    }
};

function OperatorItem(props) {
    const {
        color, id, onDelete, title, subtitle
    } = props;

    return (
        <Box
            sx={{
                padding: '8px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #c7c6c5'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '8px'
                }}
            >
                <IconDotFilled color={color}/>
                <Stack
                    flex='1'
                    minWidth={0}
                >
                    {title}
                    {
                        subtitle ? (
                            <Typography variant='subtext'>
                                {subtitle}
                            </Typography>
                        ) : null
                    }
                </Stack>
            </Box>
            <IconButton
                onClick={() => onDelete(id)}
                variant={'borderless'}
            >
                <IconRemove
                    {...REMOVE_ICON_STYLE}
                    primary='#5E5E5D'
                />
            </IconButton>
        </Box>
    );
}

OperatorItem.propTypes = {
    color: PropTypes.string,
    id: PropTypes.string,
    onDelete: PropTypes.func,
    subtitle: PropTypes.string,
    title: PropTypes.string
};

function OperatorGroup(props) {
    const {
        group, onDelete
    } = props;

    const {
        id, items, title
    } = group;

    return (
        <Box
            sx={{
                paddingBottom: '16px'
            }}
        >
            <Typography
                display={'block'}
                sx={{
                    marginBottom: '8px'
                }}
                variant={'labelMedium'}
            >
                {title}
            </Typography>
            <Stack
                direction={'column'}
                sx={{
                    border: '1px solid #c7c6c5',
                    borderBottom: 'none',
                    borderRadius: '4px'
                }}
            >
                {items.map((item) => (
                    <OperatorItem
                        color={id}
                        id={item.id}
                        key={item.id}
                        onDelete={onDelete}
                        subtitle={item.subtitle}
                        title={item.title}
                    />
                ))}
            </Stack>
        </Box>
    );
}

OperatorGroup.propTypes = {
    group: PropTypes.array,
    onDelete: PropTypes.func
};

function WorkboardOperators(props) {
    const {
        deleteOperator,
        loading,
        membership,
        orgId,
        operators,
        selectOperator,
        selectedOperators,
        translations,
        userRoles
    } = props;

    const MULTI_SELECT_LABELS = {
        placeholder: translations.ONLINK_SELECT_OPERATORS,
        multiSelect: translations.ONLINK_OPERATORS_SELECTED,
        emptyList: translations.ONLINK_WORKBOARD_NO_RESULTS,
        noItemMatch: translations.NO_RESULTS_FOUND,
        allSelect: translations.ALL_SELECTED
    };

    const {
        overtimeReachedThreshold, overtimeWarningThreshold
    } =
        getOvertimeThresholds(
            membership.properties.workboard_weekly_work_hours,
            membership.properties.workboard_employee_daily_hours_limit
        );

    const operatorItems = React.useMemo(() => {
        if (!membership.isMigrated) {
            return {
                items: getFormattedOperators(
                    {
                        operators,
                        overtimeReachedThreshold,
                        overtimeWarningThreshold,
                        translations
                    },
                    true
                )
            };
        }

        const {groupedOperators: groups} = getGroupedFormattedOperators(
            {
                operators,
                overtimeReachedThreshold,
                overtimeWarningThreshold,
                showIcon: true,
                translations
            },
            true
        );

        return {
            groups,
            items: [],
            groupOptions: {
                collapseAll: true
            }
        };
    }, [
        membership.isMigrated,
        operators,
        overtimeReachedThreshold,
        overtimeWarningThreshold,
        userRoles
    ]);

    const operatorsList = React.useMemo(() => {
        const {groupedOperators: groups} = getGroupedFormattedOperators(
            {
                operators: selectedOperators,
                overtimeReachedThreshold,
                overtimeWarningThreshold,
                translations
            },
            true
        );

        return groups;
    }, [
        selectedOperators,
        operators,
        overtimeReachedThreshold,
        overtimeWarningThreshold
    ]);

    const [pendingSelectedIds, setPendingSelectedIds] = React.useState([]);

    const selectedIds = React.useMemo(
        () => selectedOperators.map((op) => op.id),
        [selectedOperators, operators]
    );

    const operatorItemsCount = React.useMemo(() => {
        if (operatorItems.groups) {
            return operatorItems.groups.reduce((sum, g) => sum + g.items.length, 0);
        }
        return operatorItems.items.length;
    }, [operatorItems]);

    function onChange(newIds) {
        const isSelectingAll = newIds.length === operatorItemsCount && selectedIds.length < operatorItemsCount - 1;

        if (isSelectingAll) {
            setPendingSelectedIds(newIds);
            return;
        }

        const operatorsFound = operators.filter((op) => newIds.includes(op.id));

        selectOperator({
            newOperators: [...operatorsFound]
        });
    }

    function confirmSelectAll() {
        selectOperator({
            newOperators: [...operators]
        });
        setPendingSelectedIds([]);
    }

    function cancelSelectAll() {
        setPendingSelectedIds([]);
    }

    const handleDeleteOperator = (id) => {
        deleteOperator({
            idsToRemove: [id],
            operators: selectedOperators
        });
    };

    return (
        <Stack
            direction={'column'}
            sx={{
                padding: '16px 16px 0 16px'
            }}
        >
            <Stack
                alignItems='center'
                direction='row'
                marginBottom='16px'
                spacing={1}
            >
                <h3>{translations.OPERATORS}</h3>
                <AssistanceTooltip
                    body={
                        <>
                            <Box
                                sx={{
                                    paddingBlock: '8px',
                                    paddingInline: '16px'
                                }}
                            >
                                <Typography
                                    display={'block'}
                                    variant={'labelLarge'}
                                >
                                    {translations.ONLINK_OPERATOR_TOOLTIP_TITLE}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    paddingBlock: '8px',
                                    paddingInline: '16px'
                                }}
                            >
                                <Stack
                                    direction={'column'}
                                    gap={'16px'}
                                >
                                    <Typography
                                        display={'block'}
                                        variant={'bodyMedium'}
                                    >
                                        {translations.ONLINK_OPERATOR_SELECTIOR}
                                    </Typography>
                                    <Typography
                                        display={'block'}
                                        variant={'bodyMedium'}
                                    >
                                        {`${translations.ONLINK_OPERATOR_TOOLTIP_BODY} `}
                                        <Link
                                            href={`https://team.deere.com/organizations/${orgId}/organization-preferences/onlink-workboard`}
                                        >
                                            {translations.header_myOrganization}
                                        </Link>
                                        {'.'}
                                    </Typography>
                                    <Typography
                                        display={'block'}
                                        variant={'bodyMedium'}
                                    >
                                        {`${translations.ONLINK_OPERATOR_TOOLTIP_END} `}
                                        <Link
                                            href={`https://team.deere.com/organizations/${orgId}/team/team-members`}
                                        >
                                            {translations.TEAM_MANAGER_v2}
                                        </Link>
                                        {'.'}
                                    </Typography>
                                    <Typography
                                        display={'flex'}
                                        sx={{
                                            justifyContent: 'flex-end',
                                            alignItems: 'center'
                                        }}
                                        variant={'bodyMedium'}
                                    >
                                        <Link
                                            href='https://onlinesupport.turf.deere.com/operations-center-pro-golf-97645827/plan-c7c36755/labor-d77054ec/pro-golf-assign-by-color-c2be5298'
                                            rel='noopener noreferrer'
                                            target='_blank'
                                        >
                                            <>
                                                <IconNewWindow/>{' '}
                                                {
                                                    translations.ONLINK_SMS_TOOLTIP_HELP
                                                }
                                            </>
                                        </Link>
                                    </Typography>
                                </Stack>
                            </Box>
                        </>
                    }
                    className={'workboard-operators-tt'}
                >
                    <IconInfoAlert size={16}/>
                </AssistanceTooltip>
            </Stack>
            <Box
                sx={{
                    marginBottom: '16px'
                }}
            >
                <MultiSelect
                    {...operatorItems}
                    className={'operators_list'}
                    disableSorting={true}
                    footerAction={
                        pendingSelectedIds.length > 0 && (
                            <SelectAllConfirmation
                                onCancel={cancelSelectAll}
                                onConfirm={confirmSelectAll}
                                title={replaceTranslationNames(
                                    translations.ONLINK_SELECT_ALL_CONFIRMATION,
                                    {
                                        '0': String(pendingSelectedIds.length),
                                        '1': translations.OPERATORS
                                    }
                                )}
                                translations={translations}
                            />
                        )
                    }
                    helperText={translations.ONLINK_OPERATORS_HELPER_TEXT}
                    labels={MULTI_SELECT_LABELS}
                    loading={loading}
                    onChange={(selectedIds) => onChange(selectedIds)}
                    onChangeOpen={(isOpen) => {
                        if (!isOpen) {
                            setPendingSelectedIds([]);
                        }
                    }}
                    selectedIds={selectedIds}
                    title='Operators'
                />
            </Box>
            <Stack
                direction={'column'}
                spacing={'16px'}
            >
                {operatorsList.length > 0 &&
                    operatorsList.map((group) => (
                        <OperatorGroup
                            group={group}
                            key={group.id}
                            onDelete={handleDeleteOperator}
                        />
                    ))}
            </Stack>
        </Stack>
    );
}

WorkboardOperators.propTypes = {
    deleteOperator: PropTypes.func,
    loading: PropTypes.bool,
    membership: PropTypes.membership,
    operators: PropTypes.arrayOf(PropTypes.object),
    orgId: PropTypes.number,
    selectedOperators: PropTypes.array,
    selectOperator: PropTypes.func,
    translations: PropTypes.translations,
    userRoles: PropTypes.arrayOf(PropTypes.object)
};

export function mapStateToProps(state) {
    return {
        loading: state.setupWorkboard.operators.loading,
        membership: state.membership,
        operators: state.setupWorkboard.operators.teamMembers,
        orgId: state.membership.orgId,
        selectedOperators: state.setupWorkboard.operators.selected,
        userRoles: state.role.userRoles
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        deleteOperator(props) {
            return dispatch(deleteSelectedOperators(props));
        },
        selectOperator(props) {
            return dispatch(selectOperator(props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(WorkboardOperators);
